import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GetManufactureJournalList,
  GetManufactureJournalDetail,
  CreateManufactureJournal,
  UpdateManufactureJournal,
  DeleteManufactureJournal,
  GetManufactureJournalVoucherNumber,
  ApproveManufactureJournal,
} from "../../Services/ManufacturingJournalService";

/**
 * Manufacturing Journal State Interface with caching and optimization
 */
export interface ManufacturingJournalState {
  // List data
  manufacturingJournalCollection: any[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;

  // Detail data
  selectedManufacturingJournalDetail: any | null;

  // Voucher number data
  voucherNumber: any | null;

  // Loading states
  loadingList: boolean;
  loadingDetail: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;
  loadingApprove: boolean;
  loadingVoucher: boolean;

  // Error states
  error: string | null;
  errorDetail: string | null;
  errorVoucher: string | null;

  // Metadata
  sortColumn: string;
  sortDirection: string;
  searchValue: string;

  // Cache management
  cache: Map<string, CacheEntry>;
  lastFetchParams: FetchParams | null;

  // Success flags for UX
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
  approveSuccess: boolean;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number;
}

interface FetchParams {
  consumerId: number;
  firmId: number;
  branchId: number;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  financialYear: string;
  startDate: string;
  endDate: string;
}

const CACHE_DURATION = 5 * 60 * 1000;

const generateCacheKey = (params: FetchParams): string => {
  return `manufacturing_journal_list_${params.consumerId}_${params.firmId}_${params.branchId}_${params.pageIndex}_${params.searchValue}_${params.startDate}_${params.endDate}`;
};

const isCacheValid = (entry: CacheEntry): boolean => {
  return Date.now() - entry.timestamp < entry.expiresIn;
};

export const fetchManufacturingJournalList = createAsyncThunk(
  "manufacturingJournal/fetchManufacturingJournalList",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
      pageIndex: number;
      pageSize: number;
      sortColumn: string;
      sortDirection: string;
      searchValue: string;
      financialYear: string;
      startDate: string;
      endDate: string;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const root = getState() as { manufacturingJournal: ManufacturingJournalState };
      const cacheKey = generateCacheKey(params);

      if (root.manufacturingJournal.cache.has(cacheKey)) {
        const cached = root.manufacturingJournal.cache.get(cacheKey)!;
        if (isCacheValid(cached)) {
          return cached.data;
        }
      }

      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("pageIndex", params.pageIndex.toString());
      urlParams.append("pageSize", params.pageSize.toString());
      urlParams.append("sortColumn", params.sortColumn);
      urlParams.append("sortDirection", params.sortDirection);
      urlParams.append("searchValue", params.searchValue);
      urlParams.append("financialYear", params.financialYear);
      urlParams.append("startDate", params.startDate);
      urlParams.append("endDate", params.endDate);

      const result: any = await GetManufactureJournalList(urlParams.toString());

      if (result?.data?.data?.manufacturingJournalCollection) {
        return result.data.data;
      }

      return rejectWithValue("No manufacturing journal data received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ||
        error?.message ||
        error ||
        "Failed to fetch manufacturing journal list"
      );
    }
  }
);

export const fetchManufacturingJournalDetail = createAsyncThunk(
  "manufacturingJournal/fetchManufacturingJournalDetail",
  async (
    params: {
      consumerID: number;
      firmId: number;
      branchId: number;
      manufacturingJournalId: number;
      financialYear: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append("consumerID", params.consumerID.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append(
        "manufacturingJournalId",
        params.manufacturingJournalId.toString()
      );
      urlParams.append("financialYear", params.financialYear);

      const result: any = await GetManufactureJournalDetail(urlParams.toString());

      if (result?.data?.data) {
        return result.data.data;
      }

      return rejectWithValue("No manufacturing journal detail received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ||
        error?.message ||
        error ||
        "Failed to fetch manufacturing journal detail"
      );
    }
  }
);

export const fetchManufacturingJournalVoucherNumber = createAsyncThunk(
  "manufacturingJournal/fetchManufacturingJournalVoucherNumber",
  async (params: Record<string, any>, { rejectWithValue }) => {
    try {
      const urlParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        urlParams.append(key, String(value));
      });

      const result: any = await GetManufactureJournalVoucherNumber(
        urlParams.toString()
      );

      if (result?.data?.data) {
        return result.data.data;
      }

      return rejectWithValue("No voucher number received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ||
        error?.message ||
        error ||
        "Failed to fetch voucher number"
      );
    }
  }
);

export const createManufacturingJournalAsync = createAsyncThunk(
  "manufacturingJournal/createManufacturingJournal",
  async (data: any, { rejectWithValue }) => {
    try {
      const result: any = await CreateManufactureJournal(data);
      if (result) return result;
      return rejectWithValue("Failed to create manufacturing journal");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ||
        error?.message ||
        error ||
        "Failed to create manufacturing journal"
      );
    }
  }
);

export const updateManufacturingJournalAsync = createAsyncThunk(
  "manufacturingJournal/updateManufacturingJournal",
  async (data: any, { rejectWithValue }) => {
    try {
      const result: any = await UpdateManufactureJournal(data);
      if (result) return result;
      return rejectWithValue("Failed to update manufacturing journal");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ||
        error?.message ||
        error ||
        "Failed to update manufacturing journal"
      );
    }
  }
);

export const deleteManufacturingJournalAsync = createAsyncThunk(
  "manufacturingJournal/deleteManufacturingJournal",
  async (data: any, { rejectWithValue }) => {
    try {
      const result: any = await DeleteManufactureJournal(data);
      if (result?.data || result?.Data) return data;
      return rejectWithValue("Failed to delete manufacturing journal");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ||
        error?.message ||
        error ||
        "Failed to delete manufacturing journal"
      );
    }
  }
);

export const approveManufacturingJournalAsync = createAsyncThunk(
  "manufacturingJournal/approveManufacturingJournal",
  async (data: any, { rejectWithValue }) => {
    try {
      const result: any = await ApproveManufactureJournal(data);
      if (result?.data || result?.Data) return data;
      return rejectWithValue("Failed to approve manufacturing journal");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ||
        error?.message ||
        error ||
        "Failed to approve manufacturing journal"
      );
    }
  }
);

const initialState: ManufacturingJournalState = {
  manufacturingJournalCollection: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: 10,
  selectedManufacturingJournalDetail: null,
  voucherNumber: null,
  loadingList: false,
  loadingDetail: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,
  loadingApprove: false,
  loadingVoucher: false,
  error: null,
  errorDetail: null,
  errorVoucher: null,
  sortColumn: "",
  sortDirection: "asc",
  searchValue: "",
  cache: new Map(),
  lastFetchParams: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
  approveSuccess: false,
};

const manufacturingJournalSlice = createSlice({
  name: "manufacturingJournal",
  initialState,
  reducers: {
    clearManufacturingJournalData: (state) => {
      state.manufacturingJournalCollection = [];
      state.totalCount = 0;
      state.selectedManufacturingJournalDetail = null;
      state.error = null;
    },
    clearManufacturingJournalDetail: (state) => {
      state.selectedManufacturingJournalDetail = null;
      state.errorDetail = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.errorDetail = null;
      state.errorVoucher = null;
    },
    clearSuccessFlags: (state) => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
      state.approveSuccess = false;
    },
    invalidateCache: (state) => {
      state.cache.clear();
    },
    setFetchMetadata: (state, action: PayloadAction<Partial<FetchParams>>) => {
      state.searchValue = action.payload.searchValue || state.searchValue;
      state.sortColumn = action.payload.sortColumn || state.sortColumn;
      state.sortDirection = action.payload.sortDirection || state.sortDirection;
      state.pageIndex = action.payload.pageIndex || state.pageIndex;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchManufacturingJournalList.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(
        fetchManufacturingJournalList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingList = false;
          state.manufacturingJournalCollection =
            action.payload?.manufacturingJournalCollection || [];
          state.totalCount = action.payload?.totalCount || 0;
          state.pageIndex = action.payload?.pageIndex || 1;
          state.pageSize = action.payload?.pageSize || 10;
          if (state.lastFetchParams) {
            const cacheKey = generateCacheKey(state.lastFetchParams);
            state.cache.set(cacheKey, {
              data: action.payload,
              timestamp: Date.now(),
              expiresIn: CACHE_DURATION,
            });
          }
        }
      )
      .addCase(fetchManufacturingJournalList.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchManufacturingJournalDetail.pending, (state) => {
        state.loadingDetail = true;
        state.errorDetail = null;
      })
      .addCase(
        fetchManufacturingJournalDetail.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingDetail = false;
          state.selectedManufacturingJournalDetail = action.payload;
        }
      )
      .addCase(fetchManufacturingJournalDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.errorDetail = action.payload as string;
      });

    builder
      .addCase(fetchManufacturingJournalVoucherNumber.pending, (state) => {
        state.loadingVoucher = true;
        state.errorVoucher = null;
      })
      .addCase(
        fetchManufacturingJournalVoucherNumber.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingVoucher = false;
          state.voucherNumber = action.payload;
        }
      )
      .addCase(
        fetchManufacturingJournalVoucherNumber.rejected,
        (state, action) => {
          state.loadingVoucher = false;
          state.errorVoucher = action.payload as string;
        }
      );

    builder
      .addCase(createManufacturingJournalAsync.pending, (state) => {
        state.loadingCreate = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createManufacturingJournalAsync.fulfilled, (state) => {
        state.loadingCreate = false;
        state.createSuccess = true;
        state.cache.clear();
      })
      .addCase(createManufacturingJournalAsync.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateManufacturingJournalAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateManufacturingJournalAsync.fulfilled, (state) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
        state.cache.clear();
      })
      .addCase(updateManufacturingJournalAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteManufacturingJournalAsync.pending, (state) => {
        state.loadingDelete = true;
        state.error = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteManufacturingJournalAsync.fulfilled, (state) => {
        state.loadingDelete = false;
        state.deleteSuccess = true;
        state.cache.clear();
      })
      .addCase(deleteManufacturingJournalAsync.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(approveManufacturingJournalAsync.pending, (state) => {
        state.loadingApprove = true;
        state.error = null;
        state.approveSuccess = false;
      })
      .addCase(approveManufacturingJournalAsync.fulfilled, (state) => {
        state.loadingApprove = false;
        state.approveSuccess = true;
        state.cache.clear();
      })
      .addCase(approveManufacturingJournalAsync.rejected, (state, action) => {
        state.loadingApprove = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearManufacturingJournalData,
  clearManufacturingJournalDetail,
  clearErrors,
  clearSuccessFlags,
  invalidateCache,
  setFetchMetadata,
} = manufacturingJournalSlice.actions;

export default manufacturingJournalSlice.reducer;
